import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../api/client';
import Modal from '../components/shared/Modal';
import EmptyState from '../components/shared/EmptyState';
import MasterPromptPanel from '../components/documents/MasterPromptPanel';
import { FileText, Plus, Pencil, Trash2, Globe } from 'lucide-react';
import { cn } from '../utils/cn';

const DOC_TYPE_VALUES = ['faq', 'guide', 'policy', 'other'];

const DOC_TYPE_COLORS = {
  faq: 'bg-blue-100 text-blue-700',
  guide: 'bg-green-100 text-green-700',
  policy: 'bg-orange-100 text-orange-700',
  other: 'bg-gray-100 text-gray-700',
};

const emptyForm = { shop_ids: [], is_global: false, title: '', content: '', doc_type: 'other' };

export default function Documents() {
  const { t } = useTranslation();
  const [documents, setDocuments] = useState([]);
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [filterShop, setFilterShop] = useState('');
  const [formError, setFormError] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const params = {};
      if (filterShop) params.shop_id = filterShop;

      const [docsRes, shopsRes] = await Promise.all([
        api.get('/documents', { params }),
        api.get('/shops'),
      ]);
      setDocuments(docsRes.data);
      setShops(shopsRes.data);
    } catch (error) {
      console.error('Failed to fetch:', error);
    } finally {
      setLoading(false);
    }
  }, [filterShop]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyForm, shop_ids: shops[0] ? [shops[0].id] : [] });
    setFormError('');
    setModalOpen(true);
  };

  const openEdit = (doc) => {
    setEditing(doc);
    setForm({
      shop_ids: (doc.shops || []).map((s) => s.id),
      is_global: !!doc.is_global,
      title: doc.title,
      content: doc.content,
      doc_type: doc.doc_type,
    });
    setFormError('');
    setModalOpen(true);
  };

  const toggleShop = (shopId) => {
    setForm((f) => ({
      ...f,
      shop_ids: f.shop_ids.includes(shopId)
        ? f.shop_ids.filter((id) => id !== shopId)
        : [...f.shop_ids, shopId],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.is_global && form.shop_ids.length === 0) {
      setFormError(t('documents.select_shops_or_global'));
      return;
    }
    setFormError('');

    const payload = {
      title: form.title,
      content: form.content,
      doc_type: form.doc_type,
      is_global: form.is_global,
      shop_ids: form.shop_ids,
    };

    try {
      if (editing) {
        await api.put(`/documents/${editing.id}`, payload);
      } else {
        await api.post('/documents', payload);
      }
      setModalOpen(false);
      fetchData();
    } catch (error) {
      console.error('Save failed:', error);
      setFormError(error.response?.data?.error || 'Save failed');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('documents.delete_confirm'))) return;
    try { await api.delete(`/documents/${id}`); fetchData(); } catch (error) { console.error('Delete failed:', error); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" /></div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">{t('documents.title')}</h1>
        <button onClick={openCreate} className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors">
          <Plus className="h-4 w-4" /> {t('documents.add')}
        </button>
      </div>

      <div className="flex items-center gap-4 mb-4">
        <select value={filterShop} onChange={(e) => setFilterShop(e.target.value)} className="rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm">
          <option value="">{t('documents.all_shops')}</option>
          {shops.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      </div>

      <MasterPromptPanel shops={shops} documents={documents} />

      {documents.length === 0 ? (
        <EmptyState icon={FileText} title={t('documents.no_results')} description={t('documents.no_results_hint')} action={<button onClick={openCreate} className="px-4 py-2 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors">{t('documents.add')}</button>} />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {documents.map((doc) => (
            <div key={doc.id} className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-gray-900">{doc.title}</h3>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    <span className={cn('inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium', DOC_TYPE_COLORS[doc.doc_type] || DOC_TYPE_COLORS.other)}>
                      {t(`documents.type_${doc.doc_type}`, { defaultValue: doc.doc_type })}
                    </span>
                    {doc.is_global ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                        <Globe className="h-3 w-3" /> {t('documents.global_badge')}
                      </span>
                    ) : (
                      doc.shops?.map((shop) => (
                        <span key={shop.id} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                          {shop.name}
                        </span>
                      ))
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button onClick={() => openEdit(doc)} className="p-1.5 text-gray-400 hover:text-primary-600 transition-colors"><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => handleDelete(doc.id)} className="p-1.5 text-gray-400 hover:text-red-600 transition-colors"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
              <p className="text-sm text-gray-500 line-clamp-3">{doc.content}</p>
            </div>
          ))}
        </div>
      )}

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editing ? t('documents.edit_title') : t('documents.create_title')} size="lg">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <input
                type="checkbox"
                checked={form.is_global}
                onChange={(e) => setForm({ ...form, is_global: e.target.checked })}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <Globe className="h-4 w-4 text-gray-500" />
              {t('documents.is_global')}
            </label>
            <p className="mt-1 text-xs text-gray-500">{t('documents.is_global_hint')}</p>
          </div>

          {!form.is_global && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('documents.applies_to')}</label>
              <div className="flex flex-wrap gap-2">
                {shops.map((shop) => (
                  <button
                    key={shop.id}
                    type="button"
                    onClick={() => toggleShop(shop.id)}
                    className={cn(
                      'px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors',
                      form.shop_ids.includes(shop.id)
                        ? 'bg-primary-50 border-primary-300 text-primary-700'
                        : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
                    )}
                  >
                    {shop.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('documents.doc_type')}</label>
            <select value={form.doc_type} onChange={(e) => setForm({ ...form, doc_type: e.target.value })} className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500">
              {DOC_TYPE_VALUES.map((v) => <option key={v} value={v}>{t(`documents.type_${v}`)}</option>)}
            </select>
          </div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">{t('documents.title_field')} *</label><input type="text" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500" /></div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">{t('documents.content')} *</label><textarea value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} required rows={10} className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500" /></div>

          {formError && <p className="text-sm text-red-600">{formError}</p>}

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={() => setModalOpen(false)} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors">{t('common.cancel')}</button>
            <button type="submit" className="px-4 py-2 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors">{editing ? t('common.update') : t('common.create')}</button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
